import { Droppable } from 'react-beautiful-dnd';
import { Dropdown, Group, IconThreeDotsVer, Wrapper } from '@screentone/core';
import classnames from 'classnames';

import { CommonEntityProps } from 'components/datamodel/commonEntityProps';
import { useAlert } from 'contexts/alert/useAlert';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { MenuActionsKeys } from 'contexts/context-menu-actions/ContextMenuActionsContext';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useContextMenuActions } from 'contexts/context-menu-actions/useContextMenuActions';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { DROPPABLE_ID_PREFIXES } from 'contexts/drag-and-drop/dragUtils';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { usePageTypeSettingsContext } from 'contexts/page-type-settings/usePageTypeSettings';
import { BannerModuleContainer, PageModule } from 'data/generated/graphql';
import { getEffectiveItemLimits, getNumTotalItemsBeforeIndex } from 'utils/modules';
import styles from './BannerModule.module.scss';
import { ModuleItemCount } from '../components/module-item-count/ModuleItemCount';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { TreatmentTypeDropdown } from '../components/treatment-type-dropdown/TreatmentTypeDropdown';
import { UiModuleFieldsHeader } from '../components/ui-module-fields-header/UiModuleFieldsHeader';

interface ModuleProps extends CommonEntityProps {
  data: BannerModuleContainer;
}

export const BannerModule = ({
  data: module,
  parentHierarchyId,
  index,
  isHistory,
  isReadOnly = false
}: ModuleProps) => {
  const { generateHierarchyId, renderEntity } = useDataModelContext();
  const { renderActions: renderMenuActions, hasRenderActions } = useContextMenuActions();
  const hierarchyId = generateHierarchyId(module, parentHierarchyId, index);

  const { alertState } = useAlert();
  const moduleHasValidationError =
    !isHistory && alertState.pageValidationError?.moduleErrors.some((error) => error.pathToModule === hierarchyId);

  const { getTreatmentTypeSetting, currentLayout, pageTypeSetting } = usePageTypeSettingsContext();
  const pageModule = module.attributes.pageModule as PageModule;
  const treatmentTypeSetting = getTreatmentTypeSetting(pageModule);
  const { min: effectiveMin, max: effectiveMax } = getEffectiveItemLimits(pageModule, treatmentTypeSetting);

  const numTotalItemsUsed = getNumTotalItemsBeforeIndex(module, module.collection.length);

  const droppablePrefix = isHistory ? DROPPABLE_ID_PREFIXES.HISTORY : DROPPABLE_ID_PREFIXES.BANNER_MODULE_ITEMS;
  const actionsType = isHistory ? MenuActionsKeys.HistoryModule : MenuActionsKeys.DraftModule;
  const hasMenuActions = hasRenderActions(actionsType, {
    entity: module,
    hierarchyId,
    index,
    extraProperties: { numTotalItemsUsed }
  });

  const isAvailableLayoutModuleType = (currentLayout?.availableLayoutModules ?? [])
    .map((m) => m.defaultModule.uiModuleType)
    .includes((module.attributes.pageModule as PageModule).uiModuleType);

  const isDropDisabled = isHistory;

  return (
    <Wrapper
      margin={{ bottom: !isAvailableLayoutModuleType && !isHistory ? 'mlg' : 'none' }}
      data-model-hierarchy-id={hierarchyId}
      className={classnames(styles.moduleWrapper)}
      data-testid="banner-page-container"
    >
      <Group margin={{ bottom: 'xs' }} align="space-between" data-testid="banner-treatment-header">
        <Group gap="none" data-testid="banner-treatment-type-name">
          <TreatmentTypeDropdown
            hierarchyId={hierarchyId}
            pageModule={module.attributes.pageModule as PageModule}
            module={module}
            pageTypeSetting={pageTypeSetting}
            showCurrentTreatmentTypeAtTheTop
            viewOnly={isHistory}
            isReadOnly={isReadOnly}
          />
        </Group>
        {hasMenuActions && (
          <Dropdown padding={{ all: 'none' }} position="right" trigger={<IconThreeDotsVer color="asphalt" />}>
            {renderMenuActions(actionsType, {
              entity: module,
              hierarchyId,
              index,
              extraProperties: { numTotalItemsUsed }
            })}
          </Dropdown>
        )}
      </Group>
      <UiModuleFieldsHeader pageModule={module.attributes.pageModule as PageModule} />
      <Wrapper
        padding={{ all: 'xs' }}
        className={classnames(
          styles.moduleBox,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          { [styles.moduleDashedArea]: !isHistory }
        )}
        data-error={moduleHasValidationError ? 'true' : 'false'}
      >
        <Droppable droppableId={droppablePrefix + hierarchyId} isDropDisabled={isDropDisabled}>
          {(droppableProvided) => (
            <div
              data-testid="page-module-item"
              className={styles.moduleDroppableArea}
              ref={droppableProvided.innerRef}
              {...droppableProvided.droppableProps}
            >
              {module.collection.map((item, i) => {
                const availableContentSlots =
                  effectiveMax !== undefined
                    ? effectiveMax - getNumTotalItemsBeforeIndex(module, i)
                    : effectiveMin !== undefined
                      ? effectiveMin - getNumTotalItemsBeforeIndex(module, i)
                      : undefined;
                const articleHierarchyId = generateHierarchyId(item, hierarchyId, i);
                const articleActionsType = isHistory ? MenuActionsKeys.HistoryArticle : MenuActionsKeys.DraftArticle;

                return (
                  <div key={i}>
                    {renderEntity(item, {
                      hierarchyId,
                      index: i,
                      isHistory,
                      extraProps: {
                        content: { availableContentSlots, isDraft: !isHistory },
                        article: {
                          renderActions: () =>
                            renderMenuActions(articleActionsType, {
                              hierarchyId: articleHierarchyId,
                              entity: item,
                              index: i
                            })
                        },
                        module: {
                          treatmentTypeSetting
                        }
                      }
                    })}
                  </div>
                );
              })}
              {droppableProvided.placeholder}
              <ModuleItemCount
                min={effectiveMin}
                max={effectiveMax}
                showMaxInDenominator
                count={numTotalItemsUsed}
                contentTypes={treatmentTypeSetting?.contentTypes}
                considerZeroMin
                data-testid="page-module-item-count"
              />
            </div>
          )}
        </Droppable>
      </Wrapper>
    </Wrapper>
  );
};
